import { Location } from '$api-client/types.gen'
import { DateHelper } from '$utils/date'
import { formatAddress } from '$utils/address'
import { Button, Divider } from '$components'
import { BinIcon, ChevronLeftIcon, EditIcon } from '$components'
import { ElementIntent } from '$components'
import { Card, Text } from '$components'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import dayjs from 'dayjs'
import { OptionsDropdown } from '$components'
import { ConfirmationModal } from '$components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { locationDeleteMutation, locationIndexOptions } from '$api-client/@tanstack/react-query.gen'
import { useModalState } from '$components'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { useNavigate } from 'react-router'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Router } from '$utils/router'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'

export type LocationCardProps = {
  location: Location
}

export function LocationCard({ location }: LocationCardProps) {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { modalState: deleteLocationModalState, open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModalState()
  const { hasSubscriptionExpired } = useTenant()

  const { mutate: deleteLocation, isPending: isDeletingLocation } = useMutation({
    ...locationDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationIndexOptions().queryKey })
      closeDeleteConfirmationModal()
      ToastHelper.deleteSuccess(ToastType.Location)
    },
    onError: () => ToastHelper.deleteError(ToastType.Location),
  })

  const handleConfirmDelete = () => {
    deleteLocation({
      path: {
        location: deleteLocationModalState.modelId,
      },
    })
  }

  return (
    <>
      <Card rounding="xl">
        <Card.Content>
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <Text weight="medium">{location.name}</Text>
              <Text type="s">{formatAddress(location.address)}</Text>
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                onClick={() => setIsOpen(!isOpen)}
                className="w-fit hover:bg-transparent border border-transparent hover:border-gray-100 !rounded-[10px] !p-[7px]"
              >
                <motion.div
                  animate={{ rotate: isOpen ? 90 : -90 }}
                  transition={{ duration: 0.15 }}
                >
                  <ChevronLeftIcon className="size-4" />
                </motion.div>
              </Button>

              <OptionsDropdown options={[
                {
                  id: 'edit',
                  value: 'Edit',
                  icon: EditIcon,
                  onClick: () => navigate(Router.getRoute(SettingsRoutePath.SettingsLocationUpdate, { locationId: location.id })),
                  disabled: hasSubscriptionExpired,
                  tooltip: hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined,
                },
                {
                  id: 'delete',
                  value: 'Delete',
                  intent: ElementIntent.Danger,
                  icon: BinIcon,
                  onClick: () => openDeleteConfirmationModal(location.id),
                },
              ]}
              />
            </div>
          </div>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <div className="flex flex-col">
                  <div className="my-6">
                    <Divider />
                  </div>

                  <Text
                    type="s"
                    className="font-medium mb-2"
                  >Business Hours</Text>

                  {location.business_hours
                    .sort((a, b) => a.day_of_week - b.day_of_week)
                    .map((businessHour, key) => (
                      <div
                        key={key}
                        className="flex items-center justify-between py-2"
                      >
                        <Text
                          type="s"
                          className="font-medium w-24"
                        >{DateHelper.getWeekday(businessHour.day_of_week)}</Text>
                        <Text type="s">
                          {dayjs.utc(`2000-01-01 ${businessHour.open}`).format('h:mm A')} - {dayjs.utc(`2000-01-01 ${businessHour.close}`).format('h:mm A')}
                        </Text>
                      </div>
                    ))}

                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card.Content>
      </Card>

      {deleteLocationModalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeletingLocation}
          intent={ElementIntent.Danger}
          onClose={closeDeleteConfirmationModal}
          onConfirm={handleConfirmDelete}
          title="Delete Location"
          description="Are you sure you want to delete this location?"
        />
      )}
    </>
  )
}
